import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{this.updateSlider}}\n\n<div\n  class=\'slide-panel right {{this.showSliderClass}} {{this.hideSliderClass}}\'\n  style=\'{{css-style \"z-index\" this.zIndexStyle}}\'\n>\n  {{#if @showModal}}\n    <div class=\'header-container\'>\n      <div class=\'header main-header\'>\n        <div class=\'grid-x grid-padding-x detail-item-button\'>\n          <div class=\'cell auto\'>\n            <h4>\n              {{@title}}\n            </h4>\n          </div>\n          <div class=\'cell shrink text-right\'>\n            <button type=\'button\' class=\'hdr-btn {{if this.closeTask.isRunning \"loading\"}}\' {{on \'click\' (perform this.closeTask)}} disabled={{this.closeTask.isRunning}}>\n              <FaIcon @icon=\'times\' />\n              <EmberTooltip @popperContainer=\'body\'>{{t \'close\'}}</EmberTooltip>\n            </button>\n          </div>\n        </div>\n      </div>\n    </div>\n    <main class=\'main-content\'>\n      {{yield}}\n    </main>\n    <div class=\'resize-separator right {{this.showSliderClass}} {{this.hideSliderClass}}\'></div>\n  {{/if}}\n</div>");

var _dec, _dec2, _class, _descriptor, _descriptor2;
const ANIMATION_DELAY_SHORT = 50;
const ANIMATION_DELAY_MEDIUM = 150;

/**
 * CSS class constants
 * @constant {string}
 */
const CLASS_OPEN = 'open';
const CLASS_HIDE = 'hide';
const CLASS_HIDE_HEADER = 'hide-header';

/**
 * Default z-index value for the slide panel
 * @constant {string}
 */
const DEFAULT_Z_INDEX = '10';

/**
 * SlidePanel Component
 *
 * A sliding panel component that animates in/out from the side.
 * Manages body scroll and header visibility during transitions.
 *
 * @class BbPanelComponent
 * @extends Component
 *
 * @arg {boolean} showModal - Controls panel visibility
 * @arg {Function} closeModal - Callback invoked when panel closes
 * @arg {string} [zIndex='10'] - Custom z-index for the panel
 */
let BbPanelComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _class = class BbPanelComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Current visibility class for the slider ('open' when visible)
     * @type {string|null}
     */
    _initializerDefineProperty(this, "showSliderClass", _descriptor, this);
    /**
     * Current hide class for the slider ('hide' when hidden)
     * @type {string|null}
     */
    _initializerDefineProperty(this, "hideSliderClass", _descriptor2, this);
  }
  /**
   * Returns the z-index style value
   * @returns {string} The z-index value
   */
  get zIndexStyle() {
    return this.args.zIndex ?? DEFAULT_Z_INDEX;
  }

  /**
   * Reactive getter that triggers panel open/close based on showModal arg.
   * Note: This getter has side effects by design for template reactivity.
   * @returns {null}
   */
  get updateSlider() {
    const {
      showModal
    } = this.args;
    if (showModal) {
      this.upTask.perform();
    } else if (this.showSliderClass === CLASS_OPEN) {
      this.closeTask.perform();
    }
    return null;
  }

  /**
   * Checks if the panel is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.showSliderClass === CLASS_OPEN;
  }

  /**
   * Safely adds a class to the document body
   * @param {string} className - The class to add
   * @private
   */
  _addBodyClass(className) {
    document.body?.classList.add(className);
  }

  /**
   * Safely removes a class from the document body
   * @param {string} className - The class to remove
   * @private
   */
  _removeBodyClass(className) {
    document.body?.classList.remove(className);
  }

  /**
   * Safely invokes the closeModal callback if provided
   * @private
   */
  _invokeCloseModal() {
    if (typeof this.args.closeModal === 'function') {
      this.args.closeModal();
    }
  }

  /**
   * Ensures hide-header class is always removed when component is destroyed,
   * even if the parent tears it down via {{#if}} before closeTask can run.
   */
  willDestroy() {
    super.willDestroy(...arguments);
    this._removeBodyClass(CLASS_HIDE_HEADER);
    // Cancella timeout di animazione per evitare update su component distrutto
    this.upTask?.cancelAll?.();
    this.closeTask?.cancelAll?.();
  }

  /**
   * Task to open the slide panel with animation
   * @task
   */
  *upTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    if (!this.args.showModal) {
      this.closeTask.perform();
      return;
    }
    this.hideSliderClass = null;
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this._addBodyClass(CLASS_HIDE_HEADER);
    this.showSliderClass = CLASS_OPEN;
  }

  /**
   * Task to close the slide panel with animation
   * @task
   */
  *closeTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    this.showSliderClass = null;
    this._removeBodyClass(CLASS_HIDE_HEADER);
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this.hideSliderClass = CLASS_HIDE;
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this._invokeCloseModal();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "showSliderClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "hideSliderClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return CLASS_HIDE;
  }
}), _applyDecoratedDescriptor(_class.prototype, "upTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "upTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "closeTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbPanelComponent);

export { BbPanelComponent as default };
//# sourceMappingURL=bb-panel.js.map
